import React from 'react';
import { Card, ErrorMessage, Grid, Header2Text, IconType, Notification, RotatingIcon } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { IStandardDocumentVersionData } from '../../types/order/documents/shared/interfaces';
import { IVendorDocuments, StatusSection } from '../../components/vendorDashboard/StatusSection';
import { IVendorTaxCertificationInformationData } from '../../types/vendorDashboard/IVendorTaxCertificationInformation';
import { ITaxCertificationData } from '../../ui-toolkit/taxCertifications/ITaxCertification';
import { OrderPanel } from '../../components/vendorDashboard/OrderPanel';
import { OrderDetails } from '../../components/vendorDashboard/OrderDetails';
import { DocumentBlock } from '../../components/vendorDashboard/DocumentBlock';
import { Comments } from '../../components/vendorDashboard/comments/Comments';
import { TaxCertificationBlock } from '../../components/vendorDashboard/TaxCertificationBlock';
import { ClientUrlFactory } from '../../ui-toolkit/rest/ClientUrlFactory';
import { IDocumentCategory } from '../../types/order/documents/IDocumentCategoryType';
import { ETAForm } from '../../components/vendorDashboard/payoffs/ETAForm';
import { eVendorServiceType } from '../../types/order/title/eVendorServiceType';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../API/documentCategories/DocumentCategoriesApi';

export interface IVendorTaxCertificationPageProps {
  orderId: string;
  documents: IStandardDocumentVersionData[];
  requiredDocuments: IVendorDocuments[];
  pageError?: Error | string;
  onUpdateEta: (value?: string | null) => void;
  uploadDocumentFetch: IFetchStatus;
  updateEtaFetch: IFetchStatus;
  readyToSend: boolean;
  onSendToSpruce: () => void;
  sendToSpruceFetchStatus: IFetchStatus;
  sendToSpruceError?: Error;
  taxCertRequest: IVendorTaxCertificationInformationData;
  taxCert?: ITaxCertificationData;
  onSendTaxCertToSpruce: () => void;
}

export const VendorTaxCertificationPage: React.FC<IVendorTaxCertificationPageProps> = ({
  orderId,
  pageError,
  documents,
  requiredDocuments,
  onUpdateEta,
  updateEtaFetch,
  readyToSend,
  onSendToSpruce,
  sendToSpruceFetchStatus,
  sendToSpruceError,
  taxCertRequest,
  taxCert,
  onSendTaxCertToSpruce,
}) => {
  const documentCategoryFilter = (doc: IDocumentCategory) => {
    return doc.isVendorTaxCertification;
  };

  const { data: { allCategories, documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();

  return (
    <>
      <OrderPanel
        prefix={'Tax Certification'}
        backTo={ClientUrlFactory().urlFor.vendorTaxCertifications()}
        fileNumber={taxCertRequest.fileNumber}
        createdAt={taxCertRequest.timeEnteredQueue}
        readyToSend={readyToSend}
        onSendToSpruce={onSendToSpruce}
        status={taxCertRequest.status}
        sendToSpruceFetchStatus={sendToSpruceFetchStatus}
      />
      {typeof pageError === 'string' && <ErrorMessage message={pageError} />}
      {typeof pageError !== 'string' && <ErrorMessage error={pageError} />}
      {sendToSpruceFetchStatus.fetched && !sendToSpruceError && (
        <Notification type={'success'} message={'Thank you! Spruce has been notified.'} />
      )}
      <Grid columnCount={3}>
        <OrderDetails order={taxCertRequest} />
        <Grid>
          <StatusSection status={taxCertRequest.status} vendorDocuments={requiredDocuments} />
          <Card>
            <Grid>
              <Grid templateColumns={'min-content min-content'}>
                <Header2Text>ETA</Header2Text>
                {updateEtaFetch.fetching && <RotatingIcon type={IconType.Loading} />}
              </Grid>
              <ETAForm eta={taxCertRequest.eta} onSubmit={onUpdateEta} readOnly={updateEtaFetch.fetching} />
            </Grid>
          </Card>
        </Grid>

        <Comments orderId={orderId} vendorServiceType={eVendorServiceType.taxCertificationRequest} />
      </Grid>
      <DocumentBlock
        documents={documents}
        documentCategoriesByName={documentCategoriesByName}
        documentCategories={allCategories}
        documentCategoryFilter={documentCategoryFilter}
        orderId={orderId}
      />
      <TaxCertificationBlock
        orderId={taxCertRequest.orderId}
        taxCert={taxCert}
        onSendTaxCertToSpruce={onSendTaxCertToSpruce}
      />
    </>
  );
};
